// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import * as faceapi from "face-api.js";
import { ChallengeDetails } from "@/js/RemoteStarter";
import { BlendModeChecker } from "@/js/BlendModeChecker";
import { Utils } from "@/js/Utils";

export interface DrawBoxOptions {
  readonly boxColor?: string;
  readonly lineWidth?: number;
}

export interface DrawOptions {
  readonly faceDrawBoxOptions?: DrawBoxOptions;
  readonly noseDrawBoxOptions?: DrawBoxOptions;
}

export class Drawer {
  public static COLOR_RED = "rgba(255, 0, 0, 1)";
  public static COLOR_GREEN = "rgba(0, 255, 0, 1)";
  public static COLOR_YELLOW = "rgba(255, 255, 0, 1)";

  private readonly challengeDetails: ChallengeDetails;
  private readonly canvasElement: HTMLCanvasElement;
  private readonly destinationOutModeSupported: boolean;

  constructor(challengeDetails: ChallengeDetails, canvasElement: HTMLCanvasElement) {
    this.challengeDetails = challengeDetails;
    this.canvasElement = canvasElement;
    const blendModeChecker = new BlendModeChecker();
    this.destinationOutModeSupported = blendModeChecker.testMode("destination-out");
  }

  draw(drawOptions: DrawOptions) {
    if (drawOptions.faceDrawBoxOptions) {
      const faceAreaBox = {
        x: this.challengeDetails.areaLeft,
        y: this.challengeDetails.areaTop,
        width: this.challengeDetails.areaWidth,
        height: this.challengeDetails.areaHeight
      };
      this.drawFaceBox(faceAreaBox, drawOptions.faceDrawBoxOptions);
    }
    if (drawOptions.noseDrawBoxOptions) {
      const noseAreaBox = {
        x: this.challengeDetails.noseLeft,
        y: this.challengeDetails.noseTop,
        width: this.challengeDetails.noseWidth,
        height: this.challengeDetails.noseHeight
      };
      this.drawArea(noseAreaBox, drawOptions.noseDrawBoxOptions);
    }
  }

  private drawArea(box: faceapi.IRect, drawBoxOptions: DrawBoxOptions) {
    const drawBox = new faceapi.draw.DrawBox(box, drawBoxOptions);
    drawBox.draw(this.canvasElement);
  }
  private drawFaceBox(box: faceapi.IRect, drawBoxOptions: DrawBoxOptions) {
    const ctx = this.canvasElement.getContext("2d");
    if (ctx) {
      const canvasSupportDestinationOut =
        (Utils.getConfigBooleanValue("BLENDMODE_CHECKER_ENABLED") && this.destinationOutModeSupported) ||
        this.checkIfCanvasContextSupportComposite(ctx, "destination-out");
      if (canvasSupportDestinationOut) {
        // support composite
        this.drawFaceOvalWithContrast(ctx, box, drawBoxOptions);
      } else {
        // not support composite
        this.drawFaceOvalWithoutContrast(ctx, box, drawBoxOptions);
      }
    }
  }

  private checkIfCanvasContextSupportComposite(ctx: CanvasRenderingContext2D, testValue: any) {
    const oldValue = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = testValue;
    const result = ctx.globalCompositeOperation === testValue;
    ctx.globalCompositeOperation = oldValue;
    return result;
  }

  private drawFaceOvalWithContrast(ctx: CanvasRenderingContext2D, box: faceapi.IRect, drawBoxOptions: DrawBoxOptions) {
    ctx.beginPath();
    ctx.fillStyle = "rgba(0,0,0,0.9)";
    ctx.fillRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    ctx.globalCompositeOperation = "destination-out";
    ctx.ellipse(box.x + box.width / 2, box.y + box.height / 2, box.width / 2, box.height / 2.5, 0, 0, 2 * Math.PI);
    ctx.fill();
    if (drawBoxOptions.boxColor == Drawer.COLOR_GREEN) {
      ctx.lineWidth = 2;
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = "green";
      ctx.stroke();
    }
  }

  private drawFaceOvalWithoutContrast(
    ctx: CanvasRenderingContext2D,
    box: faceapi.IRect,
    drawBoxOptions: DrawBoxOptions
  ) {
    ctx.beginPath();
    if (drawBoxOptions.boxColor == Drawer.COLOR_GREEN) {
      ctx.strokeStyle = "green";
    } else {
      ctx.strokeStyle = "black";
    }
    ctx.lineWidth = 5;
    ctx.ellipse(box.x + box.width / 2, box.y + box.height / 2, box.width / 2, box.height / 2, 0, 0, 2 * Math.PI);
    ctx.stroke();
  }
}
